import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Skeleton from '../../components/common/Skeleton';
import { 
  Users, 
  Cpu, 
  Wallet, 
  TrendingUp, 
  ArrowUpRight, 
  ArrowDownLeft, 
  ShieldCheck, 
  Clock, 
  DollarSign, 
  Layers,
  Gift,
  Copy,
  Check
} from 'lucide-react';

const TelegramIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
  </svg>
);

const WhatsappIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01zm-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.198 8.198 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.24-8.22 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.97-.15.17-.3.19-.55.07-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.17-.25.25-.42.08-.17.04-.32-.02-.45-.06-.12-.56-1.35-.77-1.85-.2-.49-.4-.42-.55-.43-.14-.01-.3-.01-.46-.01s-.42.06-.64.3c-.22.24-.85.83-.85 2.03 0 1.2.87 2.36.99 2.52.12.17 1.72 2.62 4.16 3.67.58.25 1.03.4 1.38.51.58.18 1.11.16 1.53.1.47-.07 1.47-.6 1.67-1.18.2-.58.2-1.08.14-1.18-.06-.1-.22-.16-.47-.28z"/>
  </svg>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  // Support Links State
  const [supportLinks, setSupportLinks] = useState({
    telegramSupportLink: 'https://t.me/dubaigoldx_support',
    whatsappSupportLink: 'https://wa.me/1234567890'
  });
  const [savingSupportLinks, setSavingSupportLinks] = useState(false);
  const [supportFeedback, setSupportFeedback] = useState({ type: '', text: '' });

  // Deposit USDT Wallet Address & Network State
  const [depositAddress, setDepositAddress] = useState('TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t');
  const [depositNetwork, setDepositNetwork] = useState('USDT (BEP20 - BNB Smart Chain)');
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressFeedback, setAddressFeedback] = useState({ type: '', text: '' });
  const [copiedDepositAddr, setCopiedDepositAddr] = useState(false);

  const handleCopyAdminRefLink = () => {
    const link = `${window.location.origin}?ref=${user?.referralCode || ''}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyDepositAddress = () => {
    if (!depositAddress) return;
    navigator.clipboard.writeText(depositAddress);
    setCopiedDepositAddr(true);
    setTimeout(() => setCopiedDepositAddr(false), 2000);
  };

  const fetchAdminOverview = async () => {
    try {
      const resMetrics = await axiosClient.get('/admin/metrics');
      setData(resMetrics.data.metrics);
      if (resMetrics.data.metrics?.supportLinks) {
        setSupportLinks(resMetrics.data.metrics.supportLinks);
      }
      if (resMetrics.data.metrics?.depositUSDTAddress) {
        setDepositAddress(resMetrics.data.metrics.depositUSDTAddress);
      }
      if (resMetrics.data.metrics?.depositNetwork) {
        setDepositNetwork(resMetrics.data.metrics.depositNetwork);
      }

      const resLogs = await axiosClient.get('/admin/audit-logs?limit=8');
      setRecentLogs(resLogs.data.transactions || []);
    } catch (err) {
      console.error('[Admin Dashboard Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSupportLinks = async (e) => {
    e.preventDefault();
    setSavingSupportLinks(true);
    setSupportFeedback({ type: '', text: '' });
    try {
      const res = await axiosClient.put('/admin/commission-settings', {
        telegramSupportLink: supportLinks.telegramSupportLink,
        whatsappSupportLink: supportLinks.whatsappSupportLink
      });
      setSupportFeedback({ type: 'success', text: res.data.message || 'Support links updated successfully!' });
      if (res.data.config) {
        setSupportLinks({
          telegramSupportLink: res.data.config.telegramSupportLink || supportLinks.telegramSupportLink,
          whatsappSupportLink: res.data.config.whatsappSupportLink || supportLinks.whatsappSupportLink
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update support links.';
      setSupportFeedback({ type: 'error', text: msg });
    } finally {
      setSavingSupportLinks(false);
    }
  };

  const handleSaveDepositAddress = async (e) => {
    e.preventDefault();
    if (!depositAddress || !depositAddress.trim()) {
      setAddressFeedback({ type: 'error', text: 'Please enter a valid wallet address.' });
      return;
    }
    setSavingAddress(true);
    setAddressFeedback({ type: '', text: '' });
    try {
      const res = await axiosClient.put('/admin/commission-settings', {
        depositUSDTAddress: depositAddress.trim(),
        depositNetwork: depositNetwork
      });
      setAddressFeedback({ type: 'success', text: 'Official Deposit USDT Address & Network updated successfully! Users will now see this new configuration.' });
      if (res.data.config?.depositUSDTAddress) {
        setDepositAddress(res.data.config.depositUSDTAddress);
      }
      if (res.data.config?.depositNetwork) {
        setDepositNetwork(res.data.config.depositNetwork);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update deposit settings.';
      setAddressFeedback({ type: 'error', text: msg });
    } finally {
      setSavingAddress(false);
    }
  };

  useEffect(() => {
    fetchAdminOverview();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const m = data || {};
  const users = m.users || {};
  const mt5 = m.mt5Accounts || {};
  const fin = m.financials || {};

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-6 h-6 text-[#F97316]" />
            <h2 className="text-xl font-extrabold text-[#FFFFFF]">Central Admin Overview & Control Hub</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Real-time platform financial aggregations, pending approval queues, MT5 connections, and profit distribution engine.
          </p>
        </div>

        <div className="flex items-center space-x-3 shrink-0">
          {(fin.pendingDepositsCount > 0 || fin.pendingWithdrawalsCount > 0 || mt5.pending > 0) && (
            <div className="flex items-center space-x-2 bg-[#F59E0B]/15 border border-[#F59E0B]/30 px-3.5 py-1.5 rounded-full text-xs font-bold text-[#F59E0B]">
              <Clock className="w-3.5 h-3.5 animate-spin" />
              <span>
                {fin.pendingDepositsCount + fin.pendingWithdrawalsCount + mt5.pending} Actions Required
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Master Admin Direct Referral Link - Ultra Compact Sleek Bar */}
      <div className="bg-[#121215] px-4 py-3 rounded-2xl border border-[#F97316]/30 bg-gradient-to-r from-[#121215] via-[#1A1A1E] to-[#121215] shadow-card flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex items-center space-x-2.5 shrink-0 self-start md:self-center">
          <div className="p-1.5 rounded-lg bg-[#F97316]/10 border border-[#F97316]/20">
            <Gift className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs font-bold text-white tracking-wide">Master Admin Referral Link</span>
            <span className="font-mono text-[11px] bg-[#F97316]/15 text-[#F97316] font-bold px-2 py-0.5 rounded-md border border-[#F97316]/30">
              {user?.referralCode || 'REF123456'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2 w-full md:w-auto flex-1 md:max-w-xl md:justify-end">
          <div className="bg-[#09090B] border border-[#27272A] px-3.5 py-1.5 rounded-xl text-xs font-mono text-[#F97316] font-medium truncate flex-1 md:max-w-md select-all">
            {`${window.location.origin}?ref=${user?.referralCode || ''}`}
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCopyAdminRefLink}
            className="shrink-0 flex items-center space-x-1.5 py-1.5 text-xs shadow-[0_2px_12px_rgba(249,115,22,0.3)]"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedLink ? 'Copied!' : 'Copy Link'}</span>
          </Button>
        </div>
      </div>

      {/* Admin Configurations: Deposit USDT Address & Support Links Side-by-Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Official Deposit USDT Wallet Address Management Card */}
        <div className="bg-[#121215] p-5 rounded-2xl border border-[#10B981]/30 bg-gradient-to-r from-[#121215] to-[#064E3B]/20 shadow-card space-y-3 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-[#27272A] pb-2">
              <div className="flex items-center space-x-2">
                <Wallet className="w-4 h-4 text-[#10B981]" />
                <h3 className="text-xs font-extrabold text-[#FFFFFF] uppercase tracking-wider">Official Deposit USDT Address</h3>
              </div>
              <span className="text-[10px] text-[#10B981] font-mono font-bold bg-[#10B981]/10 px-2 py-0.5 rounded-full border border-[#10B981]/30">
                {depositNetwork.includes('BEP20') ? 'BEP20 (BSC)' : depositNetwork.includes('TRC20') ? 'TRC20 (TRON)' : 'ERC20 (ETH)'} / Platform Receiving
              </span>
            </div>

            <p className="text-xs text-[#9CA3AF] mt-2">
              This address and fixed network are shown to all users on their <span className="text-white font-bold">Wallet & Deposits</span> tab and <span className="text-white font-bold">Account Activation</span> modal.
            </p>
          </div>

          {addressFeedback.text && (
            <div className={`p-2.5 rounded-xl border text-[11px] font-semibold ${
              addressFeedback.type === 'success' ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
            }`}>
              {addressFeedback.text}
            </div>
          )}

          <form onSubmit={handleSaveDepositAddress} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              {/* Network Select */}
              <div className="sm:col-span-5">
                <label className="block text-[11px] font-bold text-[#10B981] mb-1">
                  Deposit Network (Fixed for Users)
                </label>
                <select
                  value={depositNetwork}
                  onChange={(e) => setDepositNetwork(e.target.value)}
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#10B981]"
                >
                  <option value="USDT (BEP20 - BNB Smart Chain)">USDT (BEP20 - BNB Smart Chain)</option>
                  <option value="USDT (TRC20 - Tron Network)">USDT (TRC20 - Tron Network)</option>
                  <option value="USDT (ERC20 - Ethereum Network)">USDT (ERC20 - Ethereum Network)</option>
                </select>
              </div>

              {/* USDT Deposit Address */}
              <div className="sm:col-span-7">
                <label className="block text-[11px] font-bold text-[#10B981] mb-1">
                  USDT Deposit Address
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    required
                    value={depositAddress}
                    onChange={(e) => setDepositAddress(e.target.value)}
                    placeholder="Enter official USDT deposit wallet address..."
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] font-mono text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#10B981]"
                  />
                  <button
                    type="button"
                    onClick={handleCopyDepositAddress}
                    className="p-2.5 rounded-xl bg-[#09090B] border border-[#27272A] text-[#9CA3AF] hover:text-white hover:border-[#10B981] transition-colors shrink-0"
                    title="Copy Wallet Address"
                  >
                    {copiedDepositAddr ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-[#9CA3AF] truncate max-w-xs font-mono">
                Active: {depositAddress ? `${depositAddress.substring(0, 8)}...${depositAddress.substring(depositAddress.length - 6)}` : 'None'} • {depositNetwork.includes('BEP20') ? 'BEP20' : depositNetwork.includes('TRC20') ? 'TRC20' : 'ERC20'}
              </span>
              <Button variant="primary" size="sm" type="submit" disabled={savingAddress} className="text-xs bg-[#10B981] hover:bg-[#059669] text-white border-0">
                {savingAddress ? 'Saving Settings...' : 'Save Deposit Settings'}
              </Button>
            </div>
          </form>
        </div>

        {/* 24/7 Client Portal Support Links Management Card */}
        <div className="bg-[#121215] p-5 rounded-2xl border border-[#3B82F6]/30 bg-gradient-to-r from-[#121215] to-[#1E293B]/40 shadow-card space-y-3 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-[#27272A] pb-2">
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-4 h-4 text-[#3B82F6]" />
                <h3 className="text-xs font-extrabold text-[#FFFFFF] uppercase tracking-wider">Client Portal Support Links</h3>
              </div>
              <span className="text-[10px] text-[#60A5FA] font-mono font-bold bg-[#3B82F6]/10 px-2 py-0.5 rounded-full border border-[#3B82F6]/30">24/7 Help Links</span>
            </div>

            <p className="text-xs text-[#9CA3AF] mt-2">
              Direct help channels displayed to users on the Overview and Help banners for quick support.
            </p>
          </div>

          {supportFeedback.text && (
            <div className={`p-2.5 rounded-xl border text-[11px] font-semibold ${
              supportFeedback.type === 'success' ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
            }`}>
              {supportFeedback.text}
            </div>
          )}

          <form onSubmit={handleSaveSupportLinks} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Telegram Input */}
              <div>
                <label className="block text-[11px] font-bold text-[#38BDF8] mb-1 flex items-center space-x-1.5">
                  <TelegramIcon className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span>Telegram Link</span>
                </label>
                <input
                  type="url"
                  required
                  value={supportLinks.telegramSupportLink}
                  onChange={(e) => setSupportLinks({ ...supportLinks, telegramSupportLink: e.target.value })}
                  placeholder="https://t.me/your_telegram"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] font-mono text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#38BDF8]"
                />
              </div>

              {/* WhatsApp Input */}
              <div>
                <label className="block text-[11px] font-bold text-[#22C55E] mb-1 flex items-center space-x-1.5">
                  <WhatsappIcon className="w-3.5 h-3.5 text-[#22C55E]" />
                  <span>WhatsApp Link</span>
                </label>
                <input
                  type="url"
                  required
                  value={supportLinks.whatsappSupportLink}
                  onChange={(e) => setSupportLinks({ ...supportLinks, whatsappSupportLink: e.target.value })}
                  placeholder="https://wa.me/1234567890"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] font-mono text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#22C55E]"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-[#9CA3AF]">
                Updates live in user portal help banners
              </span>
              <Button variant="primary" size="sm" type="submit" disabled={savingSupportLinks} className="text-xs bg-[#3B82F6] hover:bg-[#2563EB] text-white border-0">
                {savingSupportLinks ? 'Saving...' : 'Save Support Links'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card hoverEffect>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#9CA3AF]">Total Registered Users</span>
            <Users className="w-4 h-4 text-[#F97316]" />
          </div>
          <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">{users.total || 0}</p>
          <p className="text-[10px] text-[#10B981] mt-1 font-semibold">{users.active || 0} Active Client Portals</p>
        </Card>

        <Card hoverEffect>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#9CA3AF]">Connected MT5 Accounts</span>
            <Cpu className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">{mt5.approved || 0} / {mt5.total || 0}</p>
          <p className="text-[10px] text-[#F59E0B] mt-1 font-semibold">{mt5.pending || 0} Pending Connection Review</p>
        </Card>

        <Card hoverEffect>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#9CA3AF]">System Wallet Balances</span>
            <Wallet className="w-4 h-4 text-[#10B981]" />
          </div>
          <p className="text-2xl font-extrabold text-[#10B981] mt-2">${(fin.totalWalletBalances || 0).toFixed(2)}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">Total Client Wallet Funds</p>
        </Card>

        <Card hoverEffect>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[#9CA3AF]">Profit Share Collected</span>
            <TrendingUp className="w-4 h-4 text-[#F97316]" />
          </div>
          <p className="text-2xl font-extrabold text-[#F97316] mt-2">${(fin.totalProfitShareCollected || 0).toFixed(2)}</p>
          <p className="text-[10px] text-[#10B981] mt-1 font-semibold">
            Closed PnL: ${(fin.totalClosedPnL || 0).toFixed(2)}
          </p>
        </Card>
      </div>

      {/* Secondary Financial Aggregation Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#121215] p-4 rounded-2xl border border-[#27272A]">
          <div className="flex items-center space-x-2 text-xs font-semibold text-[#9CA3AF]">
            <ArrowDownLeft className="w-4 h-4 text-[#10B981]" />
            <span>Total Approved Deposits</span>
          </div>
          <p className="text-xl font-bold text-[#FFFFFF] mt-2">${(fin.totalApprovedDeposits || 0).toFixed(2)}</p>
          <p className="text-[10px] text-[#F59E0B] mt-1">{fin.pendingDepositsCount || 0} Pending Deposit Requests</p>
        </div>

        <div className="bg-[#121215] p-4 rounded-2xl border border-[#27272A]">
          <div className="flex items-center space-x-2 text-xs font-semibold text-[#9CA3AF]">
            <ArrowUpRight className="w-4 h-4 text-[#EF4444]" />
            <span>Total Completed Withdrawals</span>
          </div>
          <p className="text-xl font-bold text-[#FFFFFF] mt-2">${(fin.totalCompletedWithdrawals || 0).toFixed(2)}</p>
          <p className="text-[10px] text-[#F59E0B] mt-1">{fin.pendingWithdrawalsCount || 0} Pending Withdrawal Requests</p>
        </div>

        <div className="bg-[#121215] p-4 rounded-2xl border border-[#27272A]">
          <div className="flex items-center space-x-2 text-xs font-semibold text-[#9CA3AF]">
            <Layers className="w-4 h-4 text-[#F97316]" />
            <span>5-Tier Referral Paid</span>
          </div>
          <p className="text-xl font-bold text-[#FFFFFF] mt-2">${(fin.totalReferralCommissionsPaid || 0).toFixed(2)}</p>
          <p className="text-[10px] text-[#10B981] mt-1">L1 4% - L5 2% Distributed</p>
        </div>
      </div>

      {/* Recent Global System Activity Feed */}
      <Card title="Live System Transaction Ledger Feed" subtitle="Real-time audit log of deposits, profit share deductions, referral payouts, and withdrawals">
        {recentLogs.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No recent transactions logged in system.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Balance Before</th>
                  <th className="py-3 px-4">Balance After</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {recentLogs.map((tx) => (
                  <tr key={tx._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-bold text-[#FFFFFF]">
                      {tx.userId?.name || 'System'} <span className="font-mono text-[10px] text-[#F59E0B]">({tx.userId?.userIdTag || '-'})</span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${
                        tx.type === 'deposit_approval' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        tx.type === 'referral_commission' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        tx.type === 'profit_deduction' ? 'bg-[#F97316]/20 text-[#F97316]' :
                        tx.type === 'withdrawal' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                        'bg-[#27272A] text-[#9CA3AF]'
                      }`}>
                        {tx.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className={`py-3.5 px-4 font-bold ${
                      tx.type === 'withdrawal' || tx.type === 'profit_deduction' 
                        ? 'text-[#EF4444]' 
                        : 'text-[#10B981]'
                    }`}>
                      {tx.amount > 0 ? `+$${tx.amount.toFixed(2)}` : `-$${Math.abs(tx.amount).toFixed(2)}`}
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF] font-mono">${tx.balanceBefore?.toFixed(2) || '0.00'}</td>
                    <td className="py-3.5 px-4 text-[#FFFFFF] font-mono font-bold">${tx.balanceAfter?.toFixed(2) || '0.00'}</td>
                    <td className="py-3.5 px-4 text-[#9CA3AF] max-w-xs truncate">{tx.notes || tx.description || '-'}</td>
                    <td className="py-3.5 px-4 text-[#9CA3AF] text-[11px] whitespace-nowrap">
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminDashboard;
